import React, { FC, ReactNode, useMemo } from 'react';
import classNames from 'classnames';
import { MdOutlineLock } from 'react-icons/md';
import { useFormikContext } from 'formik';
import { EventProductType } from 'api/dtos/events/products';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import useIdentifyLaunchDarkly from 'hooks/useIdentifyLaunchDarkly';
import { useEventEditorContext } from 'legacy/events/contexts/EventEditorContext';

type ProductTypeSelectorProps = {
  disabled?: boolean;
  isPrivate?: boolean;
  isEditing?: boolean;
};

type TypeButtonProps = {
  disabled: boolean;
  children: ReactNode;
  onClick: (id: EventProductType) => void;
  id: EventProductType;
  selected: boolean;
};

// Product type descriptions
const PRODUCT_TYPE_DESCRIPTIONS = {
  admission: 'These tickets will grant guests entry to the event.',
  addon:
    'These tickets are for items such as merch, food & drinks, shuttles, etc...',
  bundle:
    "Group multiple ticket tiers into a single price. It's a simple way to increase sales and encourage customers to buy more — for example, offer 2 General Admission tickets or a combo like Entry + After Party in one bundle."
};

// Define product types - makes it easy to add new types in the future
const PRODUCT_TYPES: Array<{ id: EventProductType; label: string }> = [
  { id: 'admission', label: 'Admission' },
  { id: 'addon', label: 'Add-on' },
  { id: 'bundle', label: 'Bundle' }
];

const TypeButton: FC<TypeButtonProps> = ({
  disabled,
  children,
  onClick,
  id,
  selected
}) => {
  const onButtonClick = () => onClick(id);

  return (
    <button
      className={classNames(
        'w-full text-center text-base font-semibold rounded-md py-2 cursor-pointer flex align-center justify-center',
        {
          'bg-white': selected
        },
        disabled && 'cursor-not-allowed text-neutral-500'
      )}
      disabled={disabled}
      type="button"
      onClick={onButtonClick}
    >
      {disabled && selected && <MdOutlineLock size={20} className="mr-1" />}
      {children}
    </button>
  );
};

const ProductTypeSelector: FC<ProductTypeSelectorProps> = ({
  disabled = false,
  isPrivate = false,
  isEditing = false
}) => {
  const { event } = useEventEditorContext();
  const { values, setFieldValue } = useFormikContext<any>();
  const flags = useFlags();

  useIdentifyLaunchDarkly({ eventId: event._id }); // registers the event as the identity in LaunchDarkly

  const onButtonClick = (selected: EventProductType) => {
    setFieldValue('type', selected);
  };

  const availableProductTypes = useMemo(() => {
    let types = PRODUCT_TYPES;

    if (isPrivate) {
      types = types.filter((type) => type.id !== 'bundle');
    }

    // Filter bundle if the feature flag is not enabled and not in edit mode
    if (!isEditing && !flags[FLAGS.ENABLE_EVENT_CHECKOUT_V3]) {
      types = types.filter((type) => type.id !== 'bundle');
    }

    return types;
  }, [isPrivate, flags[FLAGS.ENABLE_EVENT_CHECKOUT_V3], isEditing]);

  return (
    <div className="mb-6">
      <div className="w-full p-[6px] bg-gray-200 rounded-lg flex items-center justify-between">
        {availableProductTypes.map((type) => (
          <TypeButton
            key={type.id}
            disabled={disabled}
            id={type.id}
            selected={values.type === type.id}
            onClick={onButtonClick}
          >
            {type.label}
          </TypeButton>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-3">
        {PRODUCT_TYPE_DESCRIPTIONS[values.type] || ''}
      </p>
    </div>
  );
};

export default ProductTypeSelector;
